import { useEffect, useState } from 'react';

import AllowMultipleDevices from 'components/Person/AllowMultipleDevices';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import GenericFormContainer from '../Forms/GenericFormContainer';
import SelectPersonContainer from '../Person/SelectPersonContainer';

import { sortByKey } from '../../utils';
import {
  formatSelect,
  formatSelectWithFixedValues,
  lengthValidation120,
  lengthValidation250,
  maxFileSize,
  required,
  validateDate,
  validateEmail,
  validateFutureDate,
  validatePhoneNumber,
} from '../Forms/utils';
import { canEditBroker } from './utils';

import {
  CHECKBOX,
  EXPANDABLE_IMAGE_COMPONENT,
  IMAGE_UPLOAD,
  PHONE_NUMBER,
  REACT_SELECT_INPUT,
  STYLED_TEXT,
  VISITOR_DATE_PICKER,
} from '../Forms/fieldTypes';

const BrokerFormComponent = ({
  initialValues,
  onSubmit,
  onLoad,
  isUpdate,
  isEditable,
  pmcs,
  buildings,
  doors,
  isWarningShown,
  closeWarning,
  completeVisitor,
  isBuildingLevel,
  isPmcLevel,
  preselectedProperty,
  shouldBePermanent,
  hasSelectedProperty,
  formValues,
  formInitialValues,
  form,
  validate,
  match,
  broker,
  refetchPerson,
}) => {
  const { t } = useTransl();

  const hasEmail = formInitialValues?.email;
  const hasPhoneNumber = formInitialValues?.phoneNumber;
  const userAvatar = formInitialValues?.avatar;

  const isUploadDisabled =
    formValues &&
    !isEmpty(formValues.images) &&
    get(formValues, 'images', []).length >= 1;

  const isDeletePhoto = get(formValues, 'photo_deleted');

  const canBeUpdated = canEditBroker(match?.params);

  const selectedDoorsIDs = formValues?.doors;

  const [isClearable, setIsClearable] = useState(true);

  useEffect(() => {
    const selectedDoors =
      doors &&
      selectedDoorsIDs &&
      doors.filter(({ id }) => selectedDoorsIDs.includes(id));
    const clearable = selectedDoors?.some((v) => !v.defaultInBuilding);
    setIsClearable(!!clearable);
  }, [selectedDoorsIDs]);

  const formData = [
    {
      label: t('title.userAvatar'),
      keyName: 'avatar',
      fieldType: EXPANDABLE_IMAGE_COMPONENT,
      src: userAvatar,
      height: '15rem',
      validate: [],
      isHidden: !isUpdate || !userAvatar,
    },
    {
      label: t('title.firstName'),
      keyName: 'firstName',
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
      disabled: isUpdate && !isEditable,
    },
    {
      label: t('title.lastName'),
      keyName: 'lastName',
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
      disabled: isUpdate && !isEditable,
    },
    {
      keyName: 'pmc',
      label: t('title.pmc'),
      fieldType: REACT_SELECT_INPUT,
      options:
        isPmcLevel && preselectedProperty
          ? preselectedProperty
          : formatSelect(Object.values(pmcs)).sort((a, b) =>
              sortByKey(a, b, 'label'),
            ),
      validate: [],
      disabled: isUpdate || isPmcLevel,
      isHidden: isBuildingLevel,
      isClearable: true,
      multi: !isUpdate,
    },
    {
      keyName: 'buildings',
      label: t('title.building_s'),
      fieldType: REACT_SELECT_INPUT,
      options:
        isBuildingLevel && preselectedProperty
          ? preselectedProperty
          : formatSelect(Object.values(buildings)).sort((a, b) =>
              sortByKey(a, b, 'label'),
            ),
      validate: [],
      multi: !isUpdate,
      disabled: isUpdate || isBuildingLevel,
    },
    {
      keyName: 'doors',
      label: t('title.door_s'),
      fieldType: REACT_SELECT_INPUT,
      options: formatSelectWithFixedValues(
        doors,
        'name',
        'id',
        'defaultInBuilding',
      ).sort((a, b) => sortByKey(a, b, 'label')),
      validate: [],
      multi: true,
      isHidden: !hasSelectedProperty,
      isClearable,
    },
    {
      label: t('title.email'),
      keyName: 'email',
      fieldType: STYLED_TEXT,
      validate: hasEmail ? [required, validateEmail] : [validateEmail],
      disabled: isUpdate && !isEditable,
    },
    {
      label: t('title.phoneNumber'),
      keyName: 'phoneNumber',
      fieldType: PHONE_NUMBER,
      validate: hasPhoneNumber
        ? [required, validatePhoneNumber]
        : [validatePhoneNumber],
      disabled: isUpdate && !isEditable,
    },
    {
      label: t('title.images'),
      keyName: 'images',
      fieldType: IMAGE_UPLOAD,
      validate: [maxFileSize],
      disabled: isUploadDisabled || isDeletePhoto || (isUpdate && !isEditable),
    },
    {
      label: t('title.deleteBrokerPhoto'),
      keyName: 'photo_deleted',
      fieldType: CHECKBOX,
      validate: [],
      isHidden: !isUpdate || !userAvatar || !isEditable,
      disabled: isUploadDisabled,
    },
    {
      label: `${t('title.validUntil')} ${t('title.permanentBroker')}`,
      keyName: 'validUntil',
      fieldType: VISITOR_DATE_PICKER,
      validate: [validateDate, validateFutureDate],
      id: 'newVisitor',
      shouldBePermanent,
      disablePast: true,
    },
    {
      label: t('title.comments'),
      keyName: 'comments',
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation250],
    },
  ];
  return (
    <Box>
      <GenericFormContainer
        form={form}
        formData={formData}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onLoad={onLoad}
        validate={validate}
        title={isUpdate ? t('title.updateBroker') : t('title.addBroker')}
        canBeUpdated={canBeUpdated}
        isUpdate={!!isUpdate}
        isButtonDisabled={isWarningShown}
        enableReinitialize
        destroyOnUnmount
      />
      {isWarningShown ? (
        <Box mt="3rem">
          <SelectPersonContainer
            onSelect={completeVisitor}
            onCancel={closeWarning}
            formValues={formValues}
          />
        </Box>
      ) : null}
      <AllowMultipleDevices
        person={broker?.person}
        refetchPerson={refetchPerson}
      />
    </Box>
  );
};

BrokerFormComponent.defaultProps = {
  isBuildingLevel: null,
  isPmcLevel: null,
  preselectedProperty: [],
  formValues: {},
  formInitialValues: {},
};

BrokerFormComponent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  closeWarning: PropTypes.func.isRequired,
  completeVisitor: PropTypes.func.isRequired,
  shouldBePermanent: PropTypes.func.isRequired,
  initialValues: PropTypes.instanceOf(Object).isRequired,
  pmcs: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object).isRequired,
  doors: PropTypes.instanceOf(Array).isRequired,
  isUpdate: PropTypes.bool.isRequired,
  isEditable: PropTypes.bool.isRequired,
  isWarningShown: PropTypes.bool.isRequired,
  isBuildingLevel: PropTypes.bool,
  isPmcLevel: PropTypes.bool,
  preselectedProperty: PropTypes.instanceOf(Array),
  hasSelectedProperty: PropTypes.bool.isRequired,
  form: PropTypes.string.isRequired,
  formValues: PropTypes.instanceOf(Object),
  formInitialValues: PropTypes.instanceOf(Object),
  validate: PropTypes.func.isRequired,
  refetchPerson: PropTypes.func.isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  broker: PropTypes.instanceOf(Object),
};

export default BrokerFormComponent;
